'use client';

import { useEffect, useRef } from 'react';
import Swiper from 'swiper';
import { Autoplay, Navigation } from 'swiper/modules';

import { LandingSourceButton } from '@/app/shared/landing/components/landing-controls';

export const SHIP_SMART_GOOGLE_REVIEWS = [
  {
    name: 'Saidbek Bobomurodov',
    rating: 5,
    text: `Highly Recommended – Professional & Reliable!
I recently worked with Ship Smart Solutions for a complex shipping project and I couldn’t be happier with the results. From start to finish, the entire process was smooth, transparent, and stress-free.
Special thanks to Otabek, the manager, who went above and beyond. He was extremely knowledgeable, responsive, and genuinely cared about finding the best solution for my needs. He explained everything clearly, provided great options, and followed up personally to make sure everything went perfectly.
Thanks to their expertise, we saved both time and money while getting top-tier service. If you’re looking for a trustworthy shipping brokerage firm, look no further than Ship Smart Solutions. Otabek and his team are the real deal!
I’ll definitely be using them again for all future shipments.`,
  },
  {
    name: 'Saidbek Bobomurodov',
    rating: 5,
    text: 'I’ve had an excellent experience working with this brokerage. The team is professional, responsive, and always keeps communication clear and transparent. They work hard to find quality loads, provide fair rates, and handle issues quickly when they arise. Payments are processed on time, and their dispatch and brokerage staff are knowledgeable and easy to work with. As a truck driver/carrier, I truly appreciate their reliability and commitment to building long-term business relationships. I highly recommend this brokerage to anyone looking for a trustworthy and dependable logistics partner.',
  },
  {
    name: 'Frank Abagnale',
    rating: 5,
    text: 'Great and reliable cooperative people. Always on top. These guys are awesome!!! Recommend you to work with them.',
  },
  {
    name: 'Islomjon Akramov',
    rating: 5,
    text: 'I’ve had a great experience working with Ship Smart Solutions. They are a professional and reliable brokerage firm that consistently provides excellent service, competitive rates, and outstanding communication. Their team is responsive, knowledgeable, and always willing to go the extra mile to ensure everything runs smoothly. Customer service is truly top-notch, and it’s clear they care about both their customers and carriers.',
  },
  {
    name: 'James Wick',
    rating: 5,
    text: 'Very reliable brokerage company. Top service, good rates, and great flexibility with Flatbeds, Step decks, Reefers, and Dry vans.',
  },
  {
    name: 'Charlie Cole',
    rating: 5,
    text: 'The best brokerage company. They pay a very good rate.',
  },
  {
    name: 'Makhmudjon Makhkamov',
    rating: 5,
    text: 'We have had a great experience working with Ship Smart Solutions. Their team is professional, responsive, and easy to communicate with. They provide consistent freight opportunities, fair rates, and clear load information. Whenever issues arise, they are quick to assist and find solutions.',
  },
  {
    name: 'John Black',
    rating: 5,
    text: 'Great broker to work with. Kept his word, communicated clearly, and honored everything promised to the carrier. Looking forward to working together again.',
  },
  {
    name: 'Ryan Carter',
    rating: 5,
    text: 'The best ever in the US. Always glad to work with them. Keep it up',
  },
  {
    name: 'SSA CARGO INC',
    rating: 5,
    text: 'Premium class brokerage. Highly recommended. Very responsive and pays over market prices.',
  },
] as const;

export type LandingReview = {
  name: string;
  rating: number;
  text: string;
};

function SliderArrow({ direction }: { direction: 'left' | 'right' }) {
  const left = direction === 'left';

  return (
    <svg
      aria-hidden="true"
      className="!h-[27px] !w-[27px] !min-w-[27px] shrink-0 !basis-[27px]"
      fill="none"
      height="27"
      viewBox="0 0 27 27"
      width="27"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d={
          left
            ? 'M11.3481 7.47314L5.25879 13.2856L11.3481 19.0981'
            : 'M15.2234 7.47314L21.3126 13.2856L15.2234 19.0981'
        }
        stroke="#fff"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.66071"
      />
      <path
        d={left ? 'M21.3124 13.2856H5.53564' : 'M5.259 13.2856H21.0358'}
        stroke="#fff"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.66071"
      />
    </svg>
  );
}

export function LandingTestimonials({
  autoplay = true,
  containerClassName = 'mx-auto w-[min(90%,1750px)]',
  initialSlide = 6,
  reviews = SHIP_SMART_GOOGLE_REVIEWS,
}: {
  autoplay?: boolean;
  containerClassName?: string;
  initialSlide?: number;
  reviews?: readonly LandingReview[];
}) {
  const sliderRef = useRef<HTMLDivElement>(null);
  const previousRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const sliderElement = sliderRef.current;
    const previousElement = previousRef.current;
    const nextElement = nextRef.current;
    if (!sliderElement || !previousElement || !nextElement) return;

    const compactLayout = window.matchMedia('(max-width: 991.98px)').matches;
    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    const slider = new Swiper(sliderElement, {
      modules: [Autoplay, Navigation],
      slidesPerView: 1,
      slidesPerGroup: 1,
      centeredSlides: false,
      initialSlide,
      loop: true,
      spaceBetween: 24,
      autoplay:
        !autoplay || compactLayout || reducedMotion
          ? false
          : { delay: 5000, disableOnInteraction: true },
      breakpoints: {
        576: { slidesPerView: 1 },
        768: { slidesPerView: 1 },
        992: { slidesPerView: 2 },
        1200: { slidesPerView: 3 },
      },
      navigation: {
        nextEl: nextElement,
        prevEl: previousElement,
      },
    });

    return () => slider.destroy(true, true);
  }, [autoplay, initialSlide]);

  return (
    <section
      aria-labelledby="landing-testimonials-title"
      className="overflow-hidden bg-[#07182f] !pb-[clamp(88px,7vw,132px)] !pt-[clamp(78px,6vw,112px)] text-white"
    >
      <div className={containerClassName}>
        <div className="grid gap-8 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
          <div>
            <span className="!mb-4 inline-flex items-center gap-2.5 text-[0.72rem] font-bold uppercase tracking-[0.15em] text-white/[0.76]">
              Testimonials
              <span aria-hidden="true">↗</span>
            </span>
            <h2
              className="!m-0 text-balance [&&&]:!max-w-[850px] [&&&]:!text-[clamp(2.65rem,4.2vw,5rem)] [&&&]:!font-semibold [&&&]:!leading-[1.02] [&&&]:!tracking-[-0.06em] [&&&]:!text-white"
              id="landing-testimonials-title"
            >
              Trusted by Shippers and Carrier Partners
            </h2>
          </div>

          <div className="flex gap-2.5 md:!pb-1">
            <LandingSourceButton
              aria-label="Previous testimonial"
              className="swiper-btn-prev !flex !h-11 !w-11 !min-w-11 !max-w-11 !flex-none !basis-11 items-center justify-center !rounded-full !border !border-[#29405e] !bg-[#0b213f] !text-white shadow-none transition hover:!border-[#4c6d94] hover:!bg-[#133157] focus-visible:!outline focus-visible:!outline-2 focus-visible:!outline-offset-2 focus-visible:!outline-white [&&&]:!p-0"
              ref={previousRef}
              variant="text"
            >
              <SliderArrow direction="left" />
            </LandingSourceButton>
            <LandingSourceButton
              aria-label="Next testimonial"
              className="swiper-btn-next !flex !h-11 !w-11 !min-w-11 !max-w-11 !flex-none !basis-11 items-center justify-center !rounded-full !border !border-[#29405e] !bg-[#0b213f] !text-white shadow-none transition hover:!border-[#4c6d94] hover:!bg-[#133157] focus-visible:!outline focus-visible:!outline-2 focus-visible:!outline-offset-2 focus-visible:!outline-white [&&&]:!p-0"
              ref={nextRef}
              variant="text"
            >
              <SliderArrow direction="right" />
            </LandingSourceButton>
          </div>
        </div>

        <div
          className="swiper slider-testimonial !mt-11 !overflow-hidden lg:!mt-14"
          ref={sliderRef}
        >
          <div className="swiper-wrapper !items-stretch">
            {reviews.map((review, reviewIndex) => (
              <div
                className="swiper-slide !flex !h-auto"
                key={`${review.name}-${reviewIndex}`}
              >
                <article className="flex min-h-[340px] w-full flex-col rounded-[11px] bg-[#0c2240] !p-7 md:min-h-[360px] lg:!p-9">
                  <div
                    aria-label={`${review.rating} out of 5 stars`}
                    className="flex gap-1 text-[1.05rem] leading-none"
                    role="img"
                  >
                    {Array.from({ length: 5 }, (_, starIndex) => (
                      <span
                        aria-hidden="true"
                        className={
                          starIndex < review.rating
                            ? 'text-[#ffb618]'
                            : 'text-white/20'
                        }
                        key={starIndex}
                      >
                        ★
                      </span>
                    ))}
                  </div>

                  <blockquote className="!m-0 !mt-5 line-clamp-4 [&&&]:!text-[clamp(1.2rem,1.45vw,1.55rem)] [&&&]:!font-normal [&&&]:!leading-[1.35] [&&&]:!tracking-[-0.025em] [&&&]:!text-white/[0.9]">
                    “{review.text}”
                  </blockquote>

                  <div className="!mt-auto flex items-center gap-4 !pt-9">
                    <span
                      aria-hidden="true"
                      className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#172e4f] text-xs font-semibold uppercase text-white/[0.65]"
                    >
                      {review.name
                        .split(' ')
                        .slice(0, 2)
                        .map((part) => part[0])
                        .join('')
                        .toUpperCase()}
                    </span>
                    <div className="min-w-0">
                      <h3 className="!mb-1 truncate [&&&]:!text-[1rem] [&&&]:!font-semibold [&&&]:!leading-tight [&&&]:!text-white">
                        {review.name}
                      </h3>
                      <p className="!mb-0 [&&&]:!text-[0.82rem] [&&&]:!leading-tight [&&&]:!text-white/[0.62]">
                        Google reviewer
                      </p>
                    </div>
                  </div>
                </article>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
